import React, { useEffect } from 'react';
import { getLessonPageUrl } from 'constants/pages';
import { useGetLessonByIdLazyQuery } from 'types/generated/client';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { toTitleCase } from 'utils/shared/toTitleCase';
import { ConfirmationInfoProps } from 'screens/PlayerLoggedInScreen/props';
import Button, { ButtonLink } from 'components/Button';
import { Lesson } from 'components/LessonCalendar/props';
import { calendarLinks, generateCalendarUrl } from 'components/LessonCalendar/utils';
import Link from 'components/Link';
import BookedLessonCard from './BookedLessonCard';

const ConfirmationInfo: React.FC<ConfirmationInfoProps> = ({
  coach,
  bookingData,
  handleBookAnother,
}) => {
  const [getLessonByIdQuery, { data: lessonData }] = useGetLessonByIdLazyQuery();

  const lesson = lessonData?.lessonsByPk;

  useEffect(() => {
    const init = async () => {
      await getLessonByIdQuery({
        variables: {
          id: bookingData.bookedLessonId,
        },
      });
    };
    bookingData.bookedLessonId && init();
  }, [getLessonByIdQuery, bookingData.bookedLessonId]);
  return (
    <div>
      <div className="px-ds-xl max-sm:px-ds-lg">
        <div className="mb-6 flex flex-col items-center justify-center border-b border-color-border-input-lightmode pb-6 dark:border-color-border-input-darkmode">
          <img src="/images/players/check.png" alt="finish" className="h-auto max-w-20" />
          <h3 className="typography-product-display">Lesson Booked!</h3>
          <span className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            You’re all set! Here are your lesson details.
          </span>
        </div>
        <BookedLessonCard
          price={
            convertUnitPriceToFormattedPrice(
              coach.priceUnitAmountCoachDefault!,
              coach.stripeMerchantCurrency! || coach.currencyCoachDefault!,
            ).priceDisplayFull
          }
          sport={coach.defaultSport as string}
          coachName={toTitleCase(coach.fullName || '')}
          image={coach.profileImageProviderUrl || ''}
          location={bookingData.location.address}
          time={`${bookingData.currentDateTime?.date.toDateString()} at ${
            bookingData?.currentDateTime.startTime
          }`}
        />
        {lesson && (
          <div className="mt-6 border-t border-color-border-input-lightmode py-6 dark:border-color-border-input-darkmode">
            <span className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Add to Calendar
            </span>

            <div className="mt-5  flex justify-center gap-20">
              {calendarLinks.map(({ Icon, type, className }, index) => (
                <Link
                  href={generateCalendarUrl(type, lesson as Lesson)}
                  target="_blank"
                  className="cursor-pointer"
                  key={index}
                >
                  <Icon className={className} />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-ds-lg border-color-border-input-lightmode px-ds-xl py-5 dark:border-color-border-input-darkmode max-sm:flex-col max-sm:gap-ds-md sm:border-t">
        <Button
          sizeDesktop="lg"
          sizeMobile="lg"
          onClick={handleBookAnother}
          variant="secondary"
          size="lg"
        >
          Book another lesson
        </Button>
        <ButtonLink
          sizeDesktop="lg"
          sizeMobile="lg"
          href={getLessonPageUrl(bookingData.bookedLessonId)}
          variant="primary"
        >
          View your lesson
        </ButtonLink>
      </div>
    </div>
  );
};

export default ConfirmationInfo;
